import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  acceptSpaceInvitation,
  declineSpaceInvitation,
  getSpaceInvitation,
  sendSpaceInvitations,
} from '@api-client/api-client';
import type {
  SpaceInviteBulkRequest,
  SpaceInviteBulkResponse,
  SpaceInvitationDetails,
} from '@models/api';
import { useToast } from '@hooks/use-toast';
import { logger } from '@utils/logger';
import { sanitizeError } from '@utils/sanitizeError';
import { getErrorMessage, getErrorStatus } from '@utils/errorDetection';
import {
  invalidateSpaceMembershipQueries,
  type SpaceMembershipChange,
} from '@utils/invalidateSpaceMembershipQueries';
import { spaceMembersBaseKey } from '@lib/cache/spaces';

const invitationQueryKeys = {
  details: (identifier: string, token: string) => ['space-invitation', identifier, token] as const,
};

/**
 * Sweeps after a FAILED accept: 409 = already a member, so the caches are stale; no status or 5xx
 * = the POST may have committed; any other 4xx = definite rejection, nothing to refresh.
 */
export function sweepAfterFailedAccept(
  queryClient: QueryClient,
  identifier: string,
  token: string,
  status: number | undefined,
): void {
  if (!(status === 409 || status === undefined || status >= 500)) return;
  invalidateAcceptedInvitationQueries(queryClient, identifier, token, {
    kind: 'unknown',
    candidateIds: [],
  });
}

/**
 * Invalidates the query surfaces that can still show pre-accept invite or
 * membership state. Shared by the explicit invite mutation hook and the
 * post-auth executor so both entry points converge on the same cache behavior.
 *
 * The membership part is `invalidateSpaceMembershipQueries`, the one definition of what a
 * membership change sweeps (#1057); only the invitation-details key is this file's own. Callers
 * pass `change`: `these` with the accepted space's id on success, `unknown` when the accept errored
 * indeterminately (it may still have committed).
 *
 * Synchronous and fire-and-forget, like the helper it wraps.
 */
export function invalidateAcceptedInvitationQueries(
  queryClient: QueryClient,
  identifier: string,
  token: string,
  change: SpaceMembershipChange,
): void {
  invalidateSpaceMembershipQueries(queryClient, {
    change,
    // Nothing here sweeps the event root, a space event list or the my-spaces feed on the caller's
    // behalf, so the helper owns all three. (Accepting an invite joins the space, so its events
    // belong in My Calendars.)
    alreadySweptByCaller: {
      eventRoot: false,
      spaceEventLists: false,
      mySpacesFeed: false,
      heldPostPages: false,
    },
  });
  // The accept names the space only by slug, so on an indeterminate failure the roster can't be
  // named; the base sweep refetches only the mounted (active) roster.
  if (change.kind === 'unknown' && change.candidateIds.length === 0) {
    void queryClient.invalidateQueries({ queryKey: spaceMembersBaseKey() });
  }
  void queryClient.invalidateQueries({ queryKey: invitationQueryKeys.details(identifier, token) });
}

export function useSpaceInvitation(identifier: string, token: string | null) {
  return useQuery<SpaceInvitationDetails>({
    queryKey: invitationQueryKeys.details(identifier, token ?? ''),
    queryFn: async () => {
      if (!token) throw new Error('No token');
      const { data } = await getSpaceInvitation(identifier, token);
      return data;
    },
    enabled: !!token && !!identifier,
    retry: false,
  });
}

export function useSendInvitations(identifier: string) {
  const { toast } = useToast();

  return useMutation<SpaceInviteBulkResponse, Error, SpaceInviteBulkRequest>({
    mutationFn: async body => {
      const { data } = await sendSpaceInvitations(identifier, body);
      return data;
    },
    onSuccess: data => {
      const { sent, already_member, pending_member, already_invited, blocked, error } =
        data.summary;
      if (sent > 0) toast({ title: `${sent} invite${sent > 1 ? 's' : ''} sent` });
      if (already_member > 0) toast({ title: `${already_member} already a member` });
      if (pending_member > 0) toast({ title: `${pending_member} already requested to join` });
      if (already_invited > 0) toast({ title: `${already_invited} already invited` });
      if (blocked > 0) toast({ title: `${blocked} could not be invited`, variant: 'destructive' });
      if (error > 0) toast({ title: `${error} failed to send`, variant: 'destructive' });
      if (
        sent === 0 &&
        already_member === 0 &&
        pending_member === 0 &&
        already_invited === 0 &&
        blocked === 0 &&
        error === 0
      ) {
        toast({ title: 'No invites were sent', variant: 'destructive' });
      }
    },
    onError: error => {
      logger.error(`[useSendInvitations] Failed for space ${identifier}:`, sanitizeError(error));
      if (getErrorStatus(error) === 403) {
        toast({
          title: getErrorMessage(error, 'You no longer have permission to send invites'),
          variant: 'destructive',
        });
      } else {
        toast({ title: getErrorMessage(error, 'Failed to send invites'), variant: 'destructive' });
      }
    },
  });
}

export function useAcceptInvitation(identifier: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (token: string) => {
      const { data } = await acceptSpaceInvitation(identifier, { token });
      return data;
    },
    onSuccess: (member, token) => {
      invalidateAcceptedInvitationQueries(queryClient, identifier, token, {
        kind: 'these',
        ids: [member.space_id],
      });
    },
    onError: (error, token) => {
      logger.error(`[useAcceptInvitation] Failed for space ${identifier}:`, sanitizeError(error));
      const status = getErrorStatus(error);
      // AIDEV-NOTE: 403 (email mismatch) and 410 (expired) toasts are suppressed here —
      // callers MUST provide their own onError to handle these with inline UI
      // (e.g., email mismatch drawer, expired recovery screen).
      // Without a caller-level onError for 403/410, the user gets no feedback.
      if (status === 403) {
        logger.warn(
          '[useAcceptInvitation] 403 suppressed at hook level -- caller must handle inline',
        );
        // Deliberately NOT invalidated: a 403 is about the ACCEPTOR's identity, not the
        // invitation, which stays genuinely `pending` — a refetch would return the same status
        // and change nothing. Callers that route on the status (CommunityDetail's #673 PIN
        // bypass) must record the per-token refusal themselves; see `onIdentityRejected`.
        return;
      }
      if (status === 410) {
        logger.warn(
          '[useAcceptInvitation] 410 suppressed at hook level -- caller must handle inline',
        );
        // Unlike the 403 above, this one IS stale data: the server just told us the cached
        // `pending` entry is wrong. Callers route on that status, so a stale `pending` would
        // keep sending them back into the same dead-end accept instead of failing closed.
        void queryClient.invalidateQueries({
          queryKey: invitationQueryKeys.details(identifier, token),
        });
        return;
      }
      sweepAfterFailedAccept(queryClient, identifier, token, status);
      toast({ title: getErrorMessage(error, 'Failed to accept invite'), variant: 'destructive' });
    },
  });
}

export function useDeclineInvitation(identifier: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (token: string) => {
      await declineSpaceInvitation(identifier, { token });
    },
    onSuccess: (_data, token) => {
      void queryClient.invalidateQueries({
        queryKey: invitationQueryKeys.details(identifier, token),
      });
    },
    onError: error => {
      logger.error(`[useDeclineInvitation] Failed for space ${identifier}:`, sanitizeError(error));
      const status = getErrorStatus(error);
      if (status === 410) {
        toast({ title: 'This invite is no longer available', variant: 'destructive' });
      } else if (status === 409) {
        toast({
          title: getErrorMessage(error, 'This invite has already been responded to'),
          variant: 'destructive',
        });
      } else {
        toast({
          title: getErrorMessage(error, 'Failed to decline invite'),
          variant: 'destructive',
        });
      }
    },
  });
}
